import * as React from 'react'
import {
  useFloating,
  autoUpdate,
  offset,
  flip,
  shift,
  useClick,
  useDismiss,
  useRole,  
  useInteractions,
  useMergeRefs,
  Placement,
  FloatingPortal,
  FloatingFocusManager,
  FloatingArrow,
  arrow as floatingArrow,
  useHover as floatingUseHover,
} from '@floating-ui/react'
import styled from 'styled-components'

import { Popper } from './Components'

export type PopoverProps = {
  initialOpen?: boolean
  placement?: Placement
  modal?: boolean
  disableFocus?: boolean
  open?: boolean
  onOpenChange?: (open: boolean) => void
  arrow?: boolean
  useHover?: boolean  // New prop to enable hover functionality
}

function usePopover({
  initialOpen = false,
  placement = 'bottom',
  modal,
  disableFocus,
  open: controlledOpen,
  onOpenChange: setControlledOpen,
  arrow,
  useHover = false,
}: PopoverProps = {}) {
  const [uncontrolledOpen, setUncontrolledOpen] = React.useState(initialOpen)
  const [labelId, setLabelId] = React.useState<string | undefined>()
  const [descriptionId, setDescriptionId] = React.useState<string | undefined>()
  const arrowRef = React.useRef(null)

  const open = controlledOpen ?? uncontrolledOpen
  const setOpen = setControlledOpen ?? setUncontrolledOpen

  const data = useFloating({
    placement,
    open,
    onOpenChange: setOpen,
    whileElementsMounted: autoUpdate,
    middleware: [
      offset(5),
      flip({
        crossAxis: placement.includes('-'),
        fallbackAxisSideDirection: 'end',
        padding: 5,
      }),
      shift({ padding: 5 }),
      floatingArrow({
        element: arrowRef,
      }),
    ],
  })

  const context = data.context

  const click = useClick(context, {
    enabled: controlledOpen == null && !useHover,  // Disable click if hover is active
  })

  const dismiss = useDismiss(context)
  const role = useRole(context)
  const hover = floatingUseHover(context, { enabled: useHover })

  const interactions = useInteractions([click, dismiss, role, hover])

  return React.useMemo(
    () => ({
      open,
      setOpen,
      ...interactions,
      ...data,
      modal,
      disableFocus,
      labelId,
      descriptionId,
      setLabelId,
      setDescriptionId,
      arrow,
      arrowRef,
    }),
    [
      open,
      setOpen,
      interactions,
      data,
      modal,
      disableFocus,
      labelId,
      descriptionId,
      arrow,
      arrowRef,
    ]
  )
}

type PopoverContextType =
  | (ReturnType<typeof usePopover> & {
      setLabelId: React.Dispatch<React.SetStateAction<string | undefined>>
      setDescriptionId: React.Dispatch<React.SetStateAction<string | undefined>>
      arrow?: boolean
    })
  | null

const PopoverContext = React.createContext<PopoverContextType>(null)

const usePopoverContext = () => {
  const context = React.useContext(PopoverContext)

  if (context == null) {
    throw new Error('Popover components must be wrapped in <Popover />')
  }

  return context
}

export function Popover({
  children,
  modal = false,
  arrow = true,
  useHover = false,
  ...restOptions
}: {
  children: React.ReactNode
} & PopoverProps) {
  const popover = usePopover({ modal, arrow, useHover, ...restOptions })

  return (
    <PopoverContext.Provider value={popover} data-testid="popover-context-provider">
      {children}
    </PopoverContext.Provider>
  )
}

type PopoverTriggerProps = {
  children: React.ReactNode
  asChild?: boolean
  useHover?: boolean 
}

export const PopoverTrigger = React.forwardRef<
  HTMLElement,
  React.HTMLProps<HTMLElement> & PopoverTriggerProps
>(function PopoverTrigger({ children, asChild = false, useHover = false, ...props }, propRef) {
  const context = usePopoverContext()
  const childrenRef = (children as any).ref
  const ref = useMergeRefs([context.refs.setReference, propRef, childrenRef])

  const handleMouseOver = () => {
    if (useHover) context.setOpen(true)
  }

  const handleMouseLeave = () => {
    if (useHover) context.setOpen(false)
  }

  if (asChild && React.isValidElement(children)) {
    return React.cloneElement(
      children,
      context.getReferenceProps({
        ref,
        onMouseOver: handleMouseOver,
        onMouseLeave: handleMouseLeave,
        ...props,
        ...children.props,
        'data-state': context.open ? 'open' : 'closed',
      })
    )
  }

  return (
    <div
      ref={ref}
      onMouseOver={handleMouseOver}
      onMouseLeave={handleMouseLeave}
      data-state={context.open ? 'open' : 'closed'}
      {...context.getReferenceProps(props)}
      aria-expanded={undefined}
      data-testid="popover-trigger"
    >
      {children}
    </div>
  )
})

const Arrow = styled(FloatingArrow)`
  fill: var(--white);
`

export const PopoverContent = React.forwardRef<HTMLDivElement, React.HTMLProps<HTMLDivElement>>(
  function PopoverContent({ style, ...props }, propRef) {
    const { context: floatingContext, ...context } = usePopoverContext()
    const ref = useMergeRefs([context.refs.setFloating, propRef])

    if (!floatingContext.open) return null

    return (
      <FloatingPortal data-testid="floating-portal">
        <FloatingFocusManager
          context={floatingContext}
          modal={context.modal}
          disabled={context.disableFocus}
          data-testid="floating-focus-manager"
        >
          <Popper
            ref={ref}
            style={{ ...context.floatingStyles, ...style }}
            {...context.getFloatingProps(props)}
            data-testid="popper-content"
          >
            {context.arrow && (
              <Arrow
                className="popper-arrow"
                ref={context.arrowRef}
                context={floatingContext}
                stroke="var(--gray200)"
                strokeWidth={1}
                data-testid="popper-arrow"
              />
            )}
            {props.children}
          </Popper>
        </FloatingFocusManager>
      </FloatingPortal>
    )
  }
)

export const PopoverClose = React.forwardRef<
  HTMLButtonElement,
  React.ButtonHTMLAttributes<HTMLButtonElement>
>(function PopoverClose(props, ref) {
  const { setOpen } = usePopoverContext()
  return (
    <button
      type="button"
      data-testid="popover-close"
      ref={ref}
      {...props}
      onClick={(event) => {
        props.onClick?.(event)
        setOpen(false)
      }}
    />
  )
})
